import { ReactNode } from "react";
import cx from "classnames";
import { faCalendarClock, faCalendarHeart, faTruckFast } from "@fortawesome/pro-regular-svg-icons";

import { addDays, toDate, today } from "common/utils/dates";
import { Feature, UISubscriptionStatus, UserSubscriptionAction } from "common/types";
import { Destination } from "common/router/types";
import Icon from "common/components/Icon";

import { ModalStatus } from "client/common/types";
import PillButton from "client/common/components/buttons/PillButton";
import { useFeatures } from "client/clinics/hooks";
import { urls } from "client/routes";

import { ClientSubscription, ListSubscription, SubscriptionForm } from "../types";
import SubscriptionStatus from "./SubscriptionStatus";
import { getLabelForSubscriptionStatus } from "../utils";

import styles from "../styles/SubscriptionActions.module.less";

type SubscriptionActionsProps = {
  subscription: ClientSubscription | ListSubscription;
  classNames?: {
    actions?: string;
    action?: string;
  };
};

const SubscriptionActions = (props: SubscriptionActionsProps) => {
  const { subscription, classNames } = props;
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];

  const hasModifyAction = subscription.potential_user_actions.find(
    ([action]) => action === UserSubscriptionAction.Modify,
  );

  const renderNextDateValue = () => {
    if (subscription.visible_status !== UISubscriptionStatus.Active) {
      return (
        <span className={styles.status}>
          {getLabelForSubscriptionStatus(subscription.visible_status)}{" "}
          <SubscriptionStatus.Icon subscription={subscription} />
        </span>
      );
    }
    if (isEdbEnabled) {
      return subscription.active_expected_delivery_by
        ? toDate(subscription.active_expected_delivery_by)
        : "-";
    }
    return subscription.next_order_date ? toDate(subscription.next_order_date) : "-";
  };

  // NOD − 1 is the last day a client can edit the upcoming order, floored to today so an
  // early-AM visit on the NOD day never shows a past date. Hidden when the NOD is unavailable
  // (v2 sub with a NOD >30 days out or still being recalculated).
  const getEditByHelper = () => {
    if (
      !isEdbEnabled ||
      subscription.visible_status !== UISubscriptionStatus.Active ||
      !subscription.next_order_date
    ) {
      return undefined;
    }
    const editByDate = addDays(subscription.next_order_date, -1);
    const flooredEditByDate = editByDate < today() ? today() : editByDate;
    return `Make changes by ${toDate(flooredEditByDate)}`;
  };

  const actions: {
    icon: typeof faCalendarClock;
    value: ReactNode;
    label: string;
    helper?: string;
    action?: { dest: Destination; label: string };
  }[] = [
    {
      icon: faCalendarClock,
      value: renderNextDateValue(),
      label: isEdbEnabled ? "Next est. delivery" : "Next order",
      helper: getEditByHelper(),
      action: hasModifyAction && {
        dest: urls.subscriptions.detail({
          params: { id: subscription.id },
          search: {
            form: SubscriptionForm.UpdateNextOrder,
            visible: ModalStatus.Open,
          },
        }),
        label: "Change",
      },
    },
    {
      icon: faCalendarHeart,
      value: subscription.active_frequency.display,
      label: "Frequency",
      action: hasModifyAction && {
        dest: urls.subscriptions.detail({
          params: { id: subscription.id },
          search: {
            form: SubscriptionForm.UpdateFrequency,
            visible: ModalStatus.Open,
          },
        }),
        label: "Change",
      },
    },
    ...(isEdbEnabled
      ? []
      : [
          {
            icon: faTruckFast,
            value: subscription.last_order
              ? toDate(subscription.last_order.order_placed_time)
              : "-",
            label: "Last order",
            action: subscription.last_order && {
              dest: urls.orders.detail({ params: { id: subscription.last_order.id } }),
              label: "View",
            },
          },
        ]),
  ];

  return (
    <div className={cx(styles.actions, classNames?.actions)}>
      {actions.map((action) => (
        <div className={cx(styles.action, classNames?.action)} key={action.label}>
          <div className={styles.main}>
            <div className={styles.icon}>
              <Icon fixedWidth={true} icon={action.icon} />
            </div>
            <div className={styles.text}>
              <div className={styles.value}>{action.value}</div>
              <div className={styles.label}>{action.label}</div>
              {action.helper && <div className={styles.helper}>{action.helper}</div>}
            </div>
          </div>
          {action.action && (
            <PillButton
              className={styles.button}
              variant="secondary"
              size="s"
              dest={action.action.dest}
            >
              {action.action.label}
            </PillButton>
          )}
        </div>
      ))}
    </div>
  );
};

export default SubscriptionActions;
